import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';

const AuthContext = createContext(null);

// Session status check interval (30 seconds)
const SESSION_CHECK_INTERVAL = 30 * 1000;

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Helper functions for token storage
const saveTokens = (accessToken, refreshToken) => {
  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  }
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
};

const getStoredAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
const getStoredRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Set up axios interceptor to add Authorization header
const setupAxiosInterceptor = () => {
  axios.interceptors.request.use(
    (config) => {
      const token = getStoredAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

// Initialize interceptor
setupAxiosInterceptor();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionValid, setSessionValid] = useState(true);
  const sessionCheckRef = useRef(null);
  const deviceFingerprintRef = useRef(null);

  // Set up device fingerprint headers
  const setupDeviceHeaders = useCallback(async () => {
    if (!deviceFingerprintRef.current) {
      deviceFingerprintRef.current = await getDeviceFingerprint();
    }
    const fingerprint = deviceFingerprintRef.current;
    axios.defaults.headers.common['X-Device-UUID'] = fingerprint.device_uuid;
    axios.defaults.headers.common['X-Screen-Resolution'] = fingerprint.screen_resolution;
    axios.defaults.headers.common['X-Timezone'] = fingerprint.timezone;
  }, []);

  // Check session status - lightweight endpoint to detect revocation
  const checkSessionStatus = useCallback(async () => {
    try {
      await setupDeviceHeaders();
      const response = await axios.get('/custom-auth/session-status', {
        withCredentials: true
      });
      
      if (!response.data.valid) {
        console.warn('Session invalid:', response.data.reason);
        setSessionValid(false);
        
        // Auto logout if session was revoked
        if (response.data.reason === 'session_revoked' || 
            response.data.reason === 'device_mismatch') {
          await performLogout(false); // silent logout
          window.location.href = '/login?reason=' + response.data.reason;
        }
        return false;
      }
      
      setSessionValid(true);
      return true;
    } catch (error) {
      // If we can't check, assume valid to avoid false logouts
      return true;
    }
  }, [setupDeviceHeaders]);

  // Start session status polling when user is logged in
  const startSessionPolling = useCallback(() => {
    // Clear any existing interval
    if (sessionCheckRef.current) {
      clearInterval(sessionCheckRef.current);
    }
    
    // Start polling
    sessionCheckRef.current = setInterval(checkSessionStatus, SESSION_CHECK_INTERVAL);
    
    return () => {
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
      }
    };
  }, [checkSessionStatus]);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await setupDeviceHeaders();
        
        // Check if we have a stored token
        const storedToken = getStoredAccessToken();
        if (!storedToken) {
          setUser(null);
          setLoading(false);
          return;
        }
        
        // Try to fetch current user with stored token
        const response = await axios.get('/custom-auth/me', {
          withCredentials: true
        });
        
        setUser(response.data);
        startSessionPolling();
      } catch (error) {
        console.log('Not authenticated or session expired');
        // Try to refresh token
        try {
          await refreshToken();
          const response = await axios.get('/custom-auth/me', {
            withCredentials: true
          });
          setUser(response.data);
          startSessionPolling();
        } catch (refreshError) {
          // Not logged in - clear any stale tokens
          clearTokens();
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Cleanup on unmount
    return () => {
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
      }
    };
  }, [setupDeviceHeaders, startSessionPolling]);

  // Register new user
  const register = async (email, password, name, departments = []) => {
    const response = await axios.post('/custom-auth/register', {
      email,
      password,
      name,
      departments
    }, {
      withCredentials: true
    });
    return response.data;
  };

  // Login with email and password
  const login = async (email, password) => {
    const fingerprint = await getDeviceFingerprint();
    deviceFingerprintRef.current = fingerprint;
    
    // Set device headers before login
    axios.defaults.headers.common['X-Device-UUID'] = fingerprint.device_uuid;
    axios.defaults.headers.common['X-Screen-Resolution'] = fingerprint.screen_resolution;
    axios.defaults.headers.common['X-Timezone'] = fingerprint.timezone;
    
    const response = await axios.post('/custom-auth/login', {
      email,
      password,
      device_fingerprint: fingerprint
    }, {
      withCredentials: true
    });

    // Store tokens from response
    if (response.data.access_token) {
      saveTokens(response.data.access_token, response.data.refresh_token);
    }

    // Use user data from login response directly
    if (response.data.user) {
      setUser(response.data.user);
    } else {
      // Fallback to fetching user data
      const userResponse = await axios.get('/custom-auth/me', {
        withCredentials: true
      });
      setUser(userResponse.data);
    }
    
    setSessionValid(true);
    
    // Start session polling after login
    startSessionPolling();
    
    return response.data;
  };

  // Refresh access token
  const refreshToken = async () => {
    try {
      await setupDeviceHeaders();
      
      const storedRefresh = getStoredRefreshToken();
      
      const response = await axios.post('/custom-auth/refresh', {
        refresh_token: storedRefresh
      }, {
        withCredentials: true
      });
      
      // Store new tokens
      if (response.data.access_token) {
        saveTokens(response.data.access_token, response.data.refresh_token);
      }
      
      return response.data.access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearTokens();
      throw error;
    }
  };

  // Perform logout (internal)
  const performLogout = async (callApi = true) => {
    // Stop session polling
    if (sessionCheckRef.current) {
      clearInterval(sessionCheckRef.current);
      sessionCheckRef.current = null;
    }
    
    if (callApi) {
      try {
        await axios.post('/custom-auth/logout', {}, {
          withCredentials: true
        });
      } catch (error) {
        console.error('Logout API call failed:', error);
      }
    }
    
    // Clear tokens from storage
    clearTokens();
    
    // Clear user state
    setUser(null);
    setSessionValid(false);
  };

  // Public logout function
  const logout = async () => {
    await performLogout(true);
    window.location.href = '/login';
  };

  // Google OAuth login
  const googleLogin = async (credential) => {
    const fingerprint = await getDeviceFingerprint();
    deviceFingerprintRef.current = fingerprint;
    
    // Set device headers before login
    axios.defaults.headers.common['X-Device-UUID'] = fingerprint.device_uuid;
    axios.defaults.headers.common['X-Screen-Resolution'] = fingerprint.screen_resolution;
    axios.defaults.headers.common['X-Timezone'] = fingerprint.timezone;
    
    const response = await axios.post('/auth/google', {
      credential,
      device_fingerprint: fingerprint
    }, {
      withCredentials: true
    });

    // Store tokens from response
    if (response.data.access_token) {
      saveTokens(response.data.access_token, response.data.refresh_token);
    }

    if (response.data.user) {
      setUser(response.data.user);
    }
    
    setSessionValid(true);
    startSessionPolling();
    
    return response.data;
  };

  // Function to refresh user data
  const refreshUser = async () => {
    try {
      const response = await axios.get('/custom-auth/me', {
        withCredentials: true
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  };

  const isAuthenticated = !!user;

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    googleLogin,
    refreshUser,
    refreshToken,
    isAuthenticated,
    sessionValid,
    checkSessionStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
